import { useCallback, useRef } from "react";
import { usePrintoutPresets, useReloadPrintoutPresetsOnPrintout1 } from "../../hooks/usePrintoutPresets";
import { resolveActivePreset } from "./detailedPresetState";
import { isAtPresetLimit, type PrintoutPreset } from "./printoutPresets";
import { useLivePreset } from "./useLivePreset";
import { usePresetActions } from "./usePresetActions";
import { usePresetAutosave } from "./usePresetAutosave";
import { usePresetRefreshOnOpen } from "./usePresetRefreshOnOpen";

// The popup and its tests name the on-screen printout's type from here, where the state lives.
export type { LivePreset } from "./useLivePreset";

/** `unavailable`: the user can't have saved printouts (not a head nurse), so there is nothing to show. */
export type PresetListStatus = "unavailable" | "loading" | "error" | "ready";

const NO_PRESETS: PrintoutPreset[] = [];

/**
 * The Detailed printout's saved printouts (Printout 2+): which one is in use, its on-screen settings,
 * and their autosave. Printout 1 is not here — it stays the modal's own `printoutSettings` state, saved
 * through user settings exactly as before.
 *
 * Composed from focused hooks: `useLivePreset` (on-screen settings and their seeding), `usePresetAutosave`
 * (the 5s autosave), `usePresetActions` (edit, switch, add, remove, rename) and `usePresetRefreshOnOpen`.
 */
export const useDetailedPresetState = () => {
	const presetsQuery = usePrintoutPresets();
	const reloadOnPrintout1 = useReloadPrintoutPresetsOnPrintout1();

	// DEFAULT: not loaded (or not available to this user) means no saved printouts — Printout 1 still works.
	const presets = presetsQuery.data?.presets ?? NO_PRESETS;
	const activePreset = resolveActivePreset(presetsQuery.data);
	const status = presetListStatus(presetsQuery);
	const presetsRef = useRef(presets);
	presetsRef.current = presets;

	const { live, liveRef, replaceLive, sessionEditsRef } = useLivePreset(activePreset);
	const { autosave, isSaving } = usePresetAutosave(sessionEditsRef);
	const actions = usePresetActions({ liveRef, replaceLive, sessionEditsRef, autosave, presetsRef });
	const refreshOnOpen = usePresetRefreshOnOpen({
		liveRef,
		replaceLive,
		sessionEditsRef,
		// Not while the list is unavailable (not a head nurse: no query to run) or already on its way.
		canRefresh: status !== "unavailable" && !presetsQuery.isFetching,
	});

	const { refetch } = presetsQuery;
	// A failed refetch lands in the query's own error state, which the list shows — nothing to catch here.
	const retryLoad = useCallback(() => void refetch(), [refetch]);

	/**
	 * After the department's printouts replaced this user's: back on Printout 1 at once and the list
	 * reloaded. This session's edits are forgotten — they belonged to printouts that may no longer exist,
	 * and must never seed one that comes back under the same id.
	 */
	const resetToDepartmentPrintouts = useCallback(async (): Promise<void> => {
		sessionEditsRef.current.clear();
		await reloadOnPrintout1();
	}, [reloadOnPrintout1, sessionEditsRef]);

	return {
		status,
		presets,
		activePreset,
		live,
		liveRef,
		isAtLimit: isAtPresetLimit(presets.length),
		isAdding: actions.isAdding,
		isSaving,
		updatePresetSettings: actions.updatePresetSettings,
		selectPreset: actions.selectPreset,
		addPreset: actions.addPreset,
		removePreset: actions.removePreset,
		renamePreset: actions.renamePreset,
		/** Saves a waiting edit now, to the printout it was made on. */
		flushPendingSave: autosave.flush,
		/** Loads the saved printouts again after a failed load (the list never reloads on its own). */
		retryLoad,
		resetToDepartmentPrintouts,
		refreshOnOpen,
		isRetrying: presetsQuery.isFetching,
	};
};

const presetListStatus = (query: ReturnType<typeof usePrintoutPresets>): PresetListStatus => {
	if (query.data) return "ready";
	if (query.isError) return "error";
	// Fetching, or paused waiting for the network (offline): either way the list is on its way. Only a
	// query that isn't running at all — not a head nurse — is "unavailable".
	if (query.isPending && query.fetchStatus !== "idle") return "loading";
	return "unavailable";
};
